import React, { useEffect, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Info,
  Italic,
  Trash2,
} from 'lucide-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { HeroData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import {
  deleteHeroVideoBlob,
  loadHeroVideoBlob,
  saveHeroVideoBlob,
} from 'utils/websiteBuilderMediaStore';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import { ABRASOFT_HUB_URL } from 'utils/constants';
import FontColorPicker from './FontColorPicker';
import { FONT_FAMILY_OPTIONS } from './fontFamilyOptions';
import styles from './HeroSection.module.scss';

type TextAlignOption = 'left' | 'center' | 'right';

interface HeroSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): HeroData {
  const rawTitleSize = data.titleFontSize;
  const rawSubtitleSize = data.subtitleFontSize;
  const rawTitleFontFamily = data.titleFontFamily;
  const rawSubtitleFontFamily = data.subtitleFontFamily;
  const rawCtaFontFamily = data.ctaFontFamily;
  const rawTitleColor = data.titleColor;
  const rawSubtitleColor = data.subtitleColor;
  const rawCtaColor = data.ctaColor;
  const rawCtaSize = data.ctaFontSize;
  const rawMinHeight = data.minHeight;
  const rawContentMaxWidth = data.contentMaxWidth;
  const rawFullScreen = data.fullScreen;
  const rawTitleAlign = data.titleAlign;
  const rawSubtitleAlign = data.subtitleAlign;
  const rawCtaAlign = data.ctaAlign;
  const rawTitleOffsetY = data.titleOffsetY;
  const rawSubtitleOffsetY = data.subtitleOffsetY;
  const rawCtaOffsetY = data.ctaOffsetY;
  const rawBackgroundImageUrl = data.backgroundImageUrl;
  const rawBackgroundVideoUrl = data.backgroundVideoUrl;
  const rawBackgroundVideoAssetId = data.backgroundVideoAssetId;
  const rawBackgroundMediaType = data.backgroundMediaType;
  const inferredVideoFromLegacyUrl =
    typeof rawBackgroundImageUrl === 'string' &&
    /\.mp4(?:$|[?#])/i.test(rawBackgroundImageUrl);

  const titleAlign: TextAlignOption =
    rawTitleAlign === 'left' || rawTitleAlign === 'right'
      ? rawTitleAlign
      : 'right';
  const subtitleAlign: TextAlignOption =
    rawSubtitleAlign === 'left' || rawSubtitleAlign === 'right'
      ? rawSubtitleAlign
      : 'right';

  const ctaAlign: TextAlignOption =
    rawCtaAlign === 'left' ||
    rawCtaAlign === 'center' ||
    rawCtaAlign === 'right'
      ? rawCtaAlign
      : titleAlign;

  return {
    title:
      typeof data.title === 'string' ? data.title : 'Build\nSomething\nAmazing',
    subtitle:
      typeof data.subtitle === 'string'
        ? data.subtitle
        : 'Simplify your workflow and get more done with less effort.',
    ctaText: typeof data.ctaText === 'string' ? data.ctaText : 'Get Started',
    ctaLink: typeof data.ctaLink === 'string' ? data.ctaLink : ABRASOFT_HUB_URL,
    ctaFontFamily: typeof rawCtaFontFamily === 'string' ? rawCtaFontFamily : '',
    ctaColor: typeof rawCtaColor === 'string' ? rawCtaColor : '',
    ctaFontSize:
      typeof rawCtaSize === 'number' && rawCtaSize > 0 ? rawCtaSize : 17,
    ctaBold: typeof data.ctaBold === 'boolean' ? data.ctaBold : false,
    ctaItalic: typeof data.ctaItalic === 'boolean' ? data.ctaItalic : false,
    backgroundMediaType:
      rawBackgroundMediaType === 'video' || rawBackgroundMediaType === 'image'
        ? rawBackgroundMediaType
        : inferredVideoFromLegacyUrl
          ? 'video'
          : 'image',
    backgroundImageUrl:
      typeof rawBackgroundImageUrl === 'string' && !inferredVideoFromLegacyUrl
        ? rawBackgroundImageUrl
        : heroPlaceholder,
    backgroundVideoUrl:
      typeof rawBackgroundVideoUrl === 'string'
        ? rawBackgroundVideoUrl
        : inferredVideoFromLegacyUrl &&
            typeof rawBackgroundImageUrl === 'string'
          ? rawBackgroundImageUrl
          : '',
    backgroundVideoAssetId:
      typeof rawBackgroundVideoAssetId === 'string'
        ? rawBackgroundVideoAssetId
        : '',
    titleFontSize:
      typeof rawTitleSize === 'number' && rawTitleSize > 0 ? rawTitleSize : 60,
    subtitleFontSize:
      typeof rawSubtitleSize === 'number' && rawSubtitleSize > 0
        ? rawSubtitleSize
        : 16,
    titleFontFamily:
      typeof rawTitleFontFamily === 'string'
        ? rawTitleFontFamily
        : "'Trebuchet MS', 'Segoe UI', sans-serif",
    subtitleFontFamily:
      typeof rawSubtitleFontFamily === 'string'
        ? rawSubtitleFontFamily
        : 'Tahoma, Geneva, sans-serif',
    titleColor: typeof rawTitleColor === 'string' ? rawTitleColor : '',
    subtitleColor: typeof rawSubtitleColor === 'string' ? rawSubtitleColor : '',
    titleOffsetY: typeof rawTitleOffsetY === 'number' ? rawTitleOffsetY : 0,
    subtitleOffsetY:
      typeof rawSubtitleOffsetY === 'number' ? rawSubtitleOffsetY : 0,
    ctaOffsetY: typeof rawCtaOffsetY === 'number' ? rawCtaOffsetY : 0,
    fullScreen: typeof rawFullScreen === 'boolean' ? rawFullScreen : true,
    titleBold: typeof data.titleBold === 'boolean' ? data.titleBold : true,
    titleItalic:
      typeof data.titleItalic === 'boolean' ? data.titleItalic : true,
    titleAlign,
    subtitleBold:
      typeof data.subtitleBold === 'boolean' ? data.subtitleBold : false,
    subtitleItalic:
      typeof data.subtitleItalic === 'boolean' ? data.subtitleItalic : false,
    subtitleAlign,
    ctaAlign,
    minHeight:
      typeof rawMinHeight === 'number' && rawMinHeight > 0 ? rawMinHeight : 100,
    contentMaxWidth:
      typeof rawContentMaxWidth === 'number' && rawContentMaxWidth > 0
        ? rawContentMaxWidth
        : 1100,
    backgroundOverlay:
      typeof data.backgroundOverlay === 'boolean'
        ? data.backgroundOverlay
        : false,
  };
}

export function HeroSectionEditor({
  data,
  onUpdateData,
}: HeroSectionEditorProps) {
  const d = getData(data);
  const bgFileInputRef = useRef<HTMLInputElement>(null);

  const titleFontSize = d.titleFontSize ?? 32;
  const subtitleFontSize = d.subtitleFontSize ?? 18;
  const ctaFontSize = d.ctaFontSize ?? 16;
  const titleFontFamily = d.titleFontFamily ?? '';
  const subtitleFontFamily = d.subtitleFontFamily ?? '';
  const ctaFontFamily = d.ctaFontFamily ?? '';
  const titleColor = d.titleColor ?? '';
  const subtitleColor = d.subtitleColor ?? '';
  const ctaColor = d.ctaColor ?? '';
  const titleOffsetY = d.titleOffsetY ?? 0;
  const subtitleOffsetY = d.subtitleOffsetY ?? 0;
  const ctaOffsetY = d.ctaOffsetY ?? 0;
  const fullScreen = d.fullScreen ?? false;
  const backgroundOverlay = d.backgroundOverlay ?? false;
  const backgroundMediaType = d.backgroundMediaType ?? 'image';
  const hasStoredMedia =
    Boolean(d.backgroundImageUrl?.trim()) ||
    Boolean(d.backgroundVideoUrl?.trim()) ||
    Boolean(d.backgroundVideoAssetId?.trim());

  const update = (key: keyof HeroData, value: unknown) => {
    onUpdateData({ ...data, [key]: value });
  };
  const updatePatch = (patch: Partial<HeroData>) => {
    onUpdateData({ ...data, ...patch });
  };

  const handleBgFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type.startsWith('video/')) {
      saveHeroVideoBlob(file)
        .then((videoAssetId) => {
          updatePatch({
            backgroundMediaType: 'video',
            backgroundVideoAssetId: videoAssetId,
            backgroundVideoUrl: '',
            backgroundImageUrl: '',
          });
        })
        .catch(() => {});
    } else {
      compressImageFile(file)
        .then((imageUrl) => {
          updatePatch({
            backgroundMediaType: 'image',
            backgroundImageUrl: imageUrl,
            backgroundVideoUrl: '',
          });
        })
        .catch(() => {});
    }
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={bgFileInputRef}
        type="file"
        accept="image/*,video/mp4"
        className={styles.fileInput}
        onChange={handleBgFileChange}
        aria-label="Upload left frame media"
      />

      <div className={styles.fontGroup}>
        <div className={styles.fullScreenToggleRow}>
          <label className={styles.fullScreenToggleLabel}>
            <input
              type="checkbox"
              className={styles.fullScreenToggleInput}
              checked={fullScreen}
              onChange={(e) => update('fullScreen', e.target.checked)}
            />
            Full screen hero
          </label>

          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="hero-fullscreen-tooltip">
                When enabled, the hero fills the entire viewport. After you
                scroll, the slider appears below.
              </Tooltip>
            }
          >
            <span className={styles.fullScreenHelpIcon} aria-hidden="true">
              <Info size={14} />
            </span>
          </OverlayTrigger>
        </div>
      </div>

      <div className={styles.fontGroup}>
        <label className={styles.fullScreenToggleLabel}>
          <input
            type="checkbox"
            className={styles.fullScreenToggleInput}
            checked={backgroundOverlay}
            onChange={(e) => update('backgroundOverlay', e.target.checked)}
          />
          Overlay background (semi-transparent)
        </label>
      </div>

      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Title:</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font
            <select
              className={styles.fontSelect}
              value={titleFontFamily}
              onChange={(e) => update('titleFontFamily', e.target.value)}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={`title-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size (px)
            <input
              type="number"
              min={16}
              max={72}
              className={styles.fontInput}
              value={titleFontSize}
              onChange={(e) =>
                update('titleFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
          <label className={`${styles.fontLabel} ${styles.offsetControl}`}>
            Offset (px)
            <input
              type="number"
              min={-120}
              max={120}
              className={styles.fontInput}
              value={titleOffsetY}
              onChange={(e) =>
                update('titleOffsetY', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={titleColor}
            onChange={(color) => update('titleColor', color)}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.titleBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('titleBold', !d.titleBold)}
            aria-label="Toggle title bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.titleItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('titleItalic', !d.titleItalic)}
            aria-label="Toggle title italic"
          >
            <Italic size={12} />
          </button>
          {(['left', 'center', 'right'] as TextAlignOption[]).map((align) => (
            <button
              key={align}
              type="button"
              className={`${styles.toolbarBtn} ${d.titleAlign === align ? styles.toolbarBtnActive : ''}`}
              onClick={() => update('titleAlign', align)}
              aria-label={`Set title alignment to ${align}`}
            >
              {align === 'left' ? (
                <AlignLeft size={12} />
              ) : align === 'center' ? (
                <AlignCenter size={12} />
              ) : (
                <AlignRight size={12} />
              )}
            </button>
          ))}
        </div>
        <textarea
          className={styles.titleInput}
          value={(data.title as string) || ''}
          onChange={(e) => update('title', e.target.value)}
          placeholder="Title placeholder"
          rows={2}
        />
      </div>
      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Subtitle:</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font
            <select
              className={styles.fontSelect}
              value={subtitleFontFamily}
              onChange={(e) => update('subtitleFontFamily', e.target.value)}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={`subtitle-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size (px)
            <input
              type="number"
              min={12}
              max={48}
              className={styles.fontInput}
              value={subtitleFontSize}
              onChange={(e) =>
                update('subtitleFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
          <label className={`${styles.fontLabel} ${styles.offsetControl}`}>
            Offset (px)
            <input
              type="number"
              min={-120}
              max={120}
              className={styles.fontInput}
              value={subtitleOffsetY}
              onChange={(e) =>
                update('subtitleOffsetY', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={subtitleColor}
            onChange={(color) => update('subtitleColor', color)}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.subtitleBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('subtitleBold', !d.subtitleBold)}
            aria-label="Toggle subtitle bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.subtitleItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('subtitleItalic', !d.subtitleItalic)}
            aria-label="Toggle subtitle italic"
          >
            <Italic size={12} />
          </button>
          {(['left', 'center', 'right'] as TextAlignOption[]).map((align) => (
            <button
              key={align}
              type="button"
              className={`${styles.toolbarBtn} ${d.subtitleAlign === align ? styles.toolbarBtnActive : ''}`}
              onClick={() => update('subtitleAlign', align)}
              aria-label={`Set subtitle alignment to ${align}`}
            >
              {align === 'left' ? (
                <AlignLeft size={12} />
              ) : align === 'center' ? (
                <AlignCenter size={12} />
              ) : (
                <AlignRight size={12} />
              )}
            </button>
          ))}
        </div>
        <textarea
          className={styles.subtitleInput}
          value={d.subtitle}
          onChange={(e) => update('subtitle', e.target.value)}
          placeholder="Subtitle placeholder"
          rows={2}
        />
      </div>
      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Button:</div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={ctaColor}
            onChange={(color) => update('ctaColor', color)}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.ctaBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('ctaBold', !d.ctaBold)}
            aria-label="Toggle button bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.ctaItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update('ctaItalic', !d.ctaItalic)}
            aria-label="Toggle button italic"
          >
            <Italic size={12} />
          </button>
          {(['left', 'center', 'right'] as TextAlignOption[]).map((align) => (
            <button
              key={align}
              type="button"
              className={`${styles.toolbarBtn} ${d.ctaAlign === align ? styles.toolbarBtnActive : ''}`}
              onClick={() => update('ctaAlign', align)}
              aria-label={`Set button alignment to ${align}`}
            >
              {align === 'left' ? (
                <AlignLeft size={12} />
              ) : align === 'center' ? (
                <AlignCenter size={12} />
              ) : (
                <AlignRight size={12} />
              )}
            </button>
          ))}
        </div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font
            <select
              className={styles.fontSelect}
              value={ctaFontFamily}
              onChange={(e) => update('ctaFontFamily', e.target.value)}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={`cta-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size (px)
            <input
              type="number"
              min={10}
              max={40}
              className={styles.fontInput}
              value={ctaFontSize}
              onChange={(e) =>
                update('ctaFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
          <label className={`${styles.fontLabel} ${styles.offsetControl}`}>
            Offset (px)
            <input
              type="number"
              min={-120}
              max={120}
              className={styles.fontInput}
              value={ctaOffsetY}
              onChange={(e) =>
                update('ctaOffsetY', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
        <div className={styles.ctaRow}>
          <input
            type="text"
            className={styles.ctaTextInput}
            value={(data.ctaText as string) || ''}
            onChange={(e) => update('ctaText', e.target.value)}
            placeholder="Button placeholder"
          />
          <input
            type="text"
            className={styles.ctaLinkInput}
            value={(data.ctaLink as string) || ''}
            onChange={(e) => update('ctaLink', e.target.value)}
            placeholder="Link"
          />
        </div>
      </div>
      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Left Frame Media (optional)</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Media type
            <select
              className={styles.fontSelect}
              value={backgroundMediaType}
              onChange={(e) =>
                update(
                  'backgroundMediaType',
                  e.target.value === 'video' ? 'video' : 'image',
                )
              }
            >
              <option value="image">Image</option>
              <option value="video">Video (MP4)</option>
            </select>
          </label>
        </div>
        <div className={styles.bgRow}>
          <input
            type="text"
            className={styles.bgInput}
            value={
              backgroundMediaType === 'video'
                ? (d.backgroundVideoUrl ?? '')
                : d.backgroundImageUrl
            }
            onChange={(e) =>
              updatePatch(
                backgroundMediaType === 'video'
                  ? {
                      backgroundVideoUrl: e.target.value,
                      backgroundVideoAssetId: '',
                    }
                  : { backgroundImageUrl: e.target.value },
              )
            }
            placeholder={
              backgroundMediaType === 'video'
                ? 'MP4 URL'
                : 'Background image URL'
            }
          />
          <button
            type="button"
            className={styles.browseBtn}
            onClick={() => bgFileInputRef.current?.click()}
          >
            Browse
          </button>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => {
              const currentAssetId = d.backgroundVideoAssetId?.trim();
              if (currentAssetId) {
                deleteHeroVideoBlob(currentAssetId).catch(() => {});
              }
              updatePatch({
                backgroundImageUrl: '',
                backgroundVideoUrl: '',
                backgroundVideoAssetId: '',
              });
            }}
            aria-label="Clear left frame media"
            title="Clear media"
            disabled={!hasStoredMedia}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </>
  );
}

interface HeroSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function HeroSection({ data }: HeroSectionProps) {
  const d = getData(data);
  const titleFontSize = d.titleFontSize ?? 32;
  const subtitleFontSize = d.subtitleFontSize ?? 18;
  const titleFontFamily = d.titleFontFamily?.trim() || '';
  const subtitleFontFamily = d.subtitleFontFamily?.trim() || '';
  const ctaFontFamily = d.ctaFontFamily?.trim() || '';
  const titleAlign = d.titleAlign ?? 'center';
  const subtitleAlign = d.subtitleAlign ?? 'center';
  const ctaAlign = d.ctaAlign ?? 'center';
  const contentMaxWidth = d.contentMaxWidth ?? 640;
  const titleOffsetY = d.titleOffsetY ?? 0;
  const subtitleOffsetY = d.subtitleOffsetY ?? 0;
  const ctaOffsetY = d.ctaOffsetY ?? 0;

  const backgroundMediaType = d.backgroundMediaType ?? 'image';
  const bgImageUrl = d.backgroundImageUrl?.trim() || '';
  const bgVideoUrl = d.backgroundVideoUrl?.trim() || '';
  const bgVideoAssetId = d.backgroundVideoAssetId?.trim() || '';
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState('');

  useEffect(() => {
    if (backgroundMediaType !== 'video' || bgVideoUrl) {
      setResolvedVideoUrl('');
      return;
    }
    if (!bgVideoAssetId) {
      setResolvedVideoUrl('');
      return;
    }

    let isCancelled = false;
    let objectUrl = '';

    loadHeroVideoBlob(bgVideoAssetId)
      .then((blob) => {
        if (isCancelled || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedVideoUrl(objectUrl);
      })
      .catch(() => {
        if (!isCancelled) setResolvedVideoUrl('');
      });

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [backgroundMediaType, bgVideoAssetId, bgVideoUrl]);

  const effectiveVideoUrl = bgVideoUrl || resolvedVideoUrl;
  const hasVideo =
    backgroundMediaType === 'video' && effectiveVideoUrl.length > 0;
  const hasImage = backgroundMediaType === 'image' && bgImageUrl.length > 0;
  const backgroundOverlay = d.backgroundOverlay ?? false;
  const style: React.CSSProperties = {
    minHeight: d.fullScreen ? '100svh' : '76vh',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: `${titleFontSize}px`,
    fontWeight: d.titleBold ? 700 : 400,
    fontStyle: d.titleItalic ? 'italic' : 'normal',
    textAlign: titleAlign,
    transform: `translateY(${titleOffsetY}px)`,
    ...(d.titleColor ? { color: d.titleColor } : {}),
    ...(titleFontFamily ? { fontFamily: titleFontFamily } : {}),
  };
  const subtitleStyle: React.CSSProperties = {
    fontSize: `${subtitleFontSize}px`,
    fontWeight: d.subtitleBold ? 700 : 400,
    fontStyle: d.subtitleItalic ? 'italic' : 'normal',
    textAlign: subtitleAlign,
    transform: `translateY(${subtitleOffsetY}px)`,
    ...(d.subtitleColor ? { color: d.subtitleColor } : {}),
    ...(subtitleFontFamily ? { fontFamily: subtitleFontFamily } : {}),
  };
  const ctaJustifyContent: React.CSSProperties['justifyContent'] =
    ctaAlign === 'left'
      ? 'flex-start'
      : ctaAlign === 'right'
        ? 'flex-end'
        : 'center';

  const ctaStyle: React.CSSProperties = {
    fontSize: `${d.ctaFontSize ?? 16}px`,
    fontWeight: d.ctaBold ? 700 : 600,
    fontStyle: d.ctaItalic ? 'italic' : 'normal',
    ...(d.ctaColor ? { color: d.ctaColor } : {}),
    ...(ctaFontFamily ? { fontFamily: ctaFontFamily } : {}),
    transform: `translateY(${ctaOffsetY}px)`,
  };

  return (
    <section
      className={`${styles.section} ${
        d.fullScreen ? styles.fullScreenHero : ''
      } ${backgroundOverlay ? styles.backgroundOverlay : ''}`}
      style={style}
    >
      <div className={styles.frameLayout}>
        <div
          className={styles.imageFrame}
          style={
            hasImage
              ? {
                  backgroundImage: `url(${bgImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : undefined
          }
        >
          {hasVideo && (
            <div className={styles.videoFrame}>
              <video
                src={effectiveVideoUrl}
                className={styles.videoElement}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
              />
            </div>
          )}
          {!hasImage && !hasVideo && (
            <div className={styles.imagePlaceholder}>Upload media</div>
          )}
        </div>
        <div className={styles.contentFrame}>
          <div
            className={styles.contentFrameInner}
            style={{ maxWidth: `${contentMaxWidth}px` }}
          >
            <h1 className={styles.title} style={titleStyle}>
              {d.title}
            </h1>
            <p className={styles.subtitle} style={subtitleStyle}>
              {d.subtitle}
            </p>
            <div
              className={styles.ctaRowFrame}
              style={{ justifyContent: ctaJustifyContent }}
            >
              <a href={d.ctaLink} className={styles.cta} style={ctaStyle}>
                {d.ctaText}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
